/**
 * DOMI-CARE - Gestión de Licenciamiento, Modo Demo y Seguridad SuperAdmin
 * Controla la activación comercial mediante PIN exclusivo (9891 por defecto)
 * y la persistencia en LocalStorage.
 */

import { appsScriptClient } from '../api/appsScriptClient.js';
import { authState } from './authState.js';
import { offlineStore } from '../api/offlineStore.js';
import { SEED_DATA } from '../data/seedData.js';

const STORAGE_KEY_LICENSE = 'domicare_license_info';
const STORAGE_KEY_PIN = 'domicare_superadmin_pin';
const DEFAULT_SUPERADMIN_PIN = '9891';

class LicenseState {
  constructor() {
    this.state = {
      isDemoMode: true,
      isLicensed: false,
      googleSheetUrl: '',
      companyName: 'Institución Médica Demo',
      ownerUser: null,
      activationDate: null
    };

    this.init();
  }

  init() {
    // 1. Inicializar PIN si no existe
    if (!localStorage.getItem(STORAGE_KEY_PIN)) {
      localStorage.setItem(STORAGE_KEY_PIN, DEFAULT_SUPERADMIN_PIN);
    }

    // 2. Cargar estado de licencia guardado
    const saved = localStorage.getItem(STORAGE_KEY_LICENSE);
    if (saved) {
      try {
        this.state = JSON.parse(saved);
        if (this.state.googleSheetUrl) {
          appsScriptClient.setApiUrl(this.state.googleSheetUrl);
        }
      } catch (e) {
        console.error('Error al parsear estado de licencia:', e);
      }
    }
  }

  isDemo() {
    return this.state.isDemoMode;
  }

  isLicensed() {
    return this.state.isLicensed;
  }

  getLicenseInfo() {
    return { ...this.state };
  }

  verifyPin(inputPin) {
    const currentPin = localStorage.getItem(STORAGE_KEY_PIN) || DEFAULT_SUPERADMIN_PIN;
    return String(inputPin).trim() === String(currentPin).trim();
  }

  changePin(newPin) {
    if (!newPin || newPin.trim().length < 4) {
      throw new Error('El PIN debe contener al menos 4 dígitos.');
    }
    localStorage.setItem(STORAGE_KEY_PIN, newPin.trim());
    return true;
  }

  async activateLicense({ googleSheetUrl, companyName, ownerName, ownerEmail, ownerPassword, migrateData }) {
    if (!googleSheetUrl || !googleSheetUrl.startsWith('https://script.google.com')) {
      throw new Error('La URL de Google Apps Script no es válida. Debe comenzar con https://script.google.com');
    }
    if (!ownerEmail || !ownerName) {
      throw new Error('Nombre del propietario y correo son obligatorios.');
    }

    // 1. Configurar cliente con la URL oficial
    appsScriptClient.setApiUrl(googleSheetUrl);

    // 2. Crear usuario propietario con privilegios de Director / Admin
    const ownerUser = {
      id: 'USR-OWNER-' + Math.floor(100 + Math.random() * 900),
      nombre: ownerName.trim(),
      email: ownerEmail.trim(),
      rol: 'Admin',
      empresa: companyName || 'Clínica de Internación Domiciliaria',
      descripcion: 'Director / Propietario de la Institución'
    };

    // 3. Actualizar estado de licencia
    this.state = {
      isDemoMode: false,
      isLicensed: true,
      googleSheetUrl: googleSheetUrl.trim(),
      companyName: companyName || 'Clínica de Internación Domiciliaria',
      ownerUser: ownerUser,
      activationDate: new Date().toISOString()
    };

    localStorage.setItem(STORAGE_KEY_LICENSE, JSON.stringify(this.state));

    // 4. Si se solicitó migrar datos locales
    if (migrateData) {
      console.log('DOMI-CARE: Migrando datos locales hacia Google Sheets...');
      await appsScriptClient.syncPendingQueue();
    }

    // 5. Dejar al nuevo propietario como usuario activo en sesión
    await authState.switchUser(ownerUser.id);

    // Notificar actualización de licencia global
    window.dispatchEvent(new CustomEvent('domicare:license-updated', { detail: this.state }));
    return true;
  }

  async resetToDemo() {
    this.state = {
      isDemoMode: true,
      isLicensed: false,
      googleSheetUrl: '',
      companyName: 'Institución Médica Demo',
      ownerUser: null,
      activationDate: null
    };

    localStorage.removeItem(STORAGE_KEY_LICENSE);
    appsScriptClient.setApiUrl('');

    // Restaurar datos semilla en IndexedDB
    await offlineStore.clearQueue();
    await offlineStore.bulkInsert('pacientes', SEED_DATA.pacientes);
    await offlineStore.bulkInsert('agenda', SEED_DATA.agendaHoy);
    await offlineStore.bulkInsert('evoluciones', SEED_DATA.evolucionesHCD);
    await offlineStore.bulkInsert('signos', SEED_DATA.signosVitalesHistorico);
    await offlineStore.bulkInsert('escalas', SEED_DATA.escalasHistorico);
    await offlineStore.bulkInsert('insumos', SEED_DATA.insumosStock);
    await offlineStore.bulkInsert('equipamiento', SEED_DATA.equipamientoComodato);

    // Cambiar a usuario demo admin inicial
    await authState.switchUser(SEED_DATA.usuarios[0].id);

    window.dispatchEvent(new CustomEvent('domicare:license-updated', { detail: this.state }));
    return true;
  }
}

export const licenseState = new LicenseState();
